import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SUBSCRIPTION_STATUS_LABELS } from '@saesak/shared';
import { AppBar, ChipRow, Screen } from '@/components/layout';
import { Button, Card, Chip, EmptyState, ErrorNote, Field, Input, Spinner } from '@/components/ui';
import { ApiError, api, apiData } from '@/lib/api';
import { formatDate, formatPhone, formatPhoneInput } from '@/lib/format';
import type { AdminFacility } from '@/types';
import { FACILITY_STATUS_LABELS, formatCount } from './lib';

const STATUS_FILTERS = ['', 'PENDING', 'APPROVED', 'SUSPENDED'] as const;

/** 기관 관리 (§3.2) — 승인·정지·사진 정책·입금 확인까지 기관 단위 운영 전부 */
export function AdminFacilitiesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['admin', 'facilities', status],
    queryFn: () => apiData<AdminFacility[]>(`/admin/facilities${status ? `?status=${status}` : ''}`),
    placeholderData: keepPreviousData,
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'facilities'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
  }

  const facilities = data ?? [];

  return (
    <Screen>
      <AppBar title="기관 관리" back />

      <div className="px-4 pt-3">
        <ChipRow>
          {STATUS_FILTERS.map((value) => (
            <Chip
              key={value || 'all'}
              tone={status === value ? 'primary' : 'neutral'}
              selected={status === value}
              onClick={() => setSearchParams(value ? { status: value } : {}, { replace: true })}
            >
              {value ? FACILITY_STATUS_LABELS[value] : '전체'}
            </Chip>
          ))}
        </ChipRow>
      </div>

      {message && (
        <p className="mx-4 mt-3 break-all rounded-sm bg-primary-soft px-3 py-2 text-sm text-primary-strong">
          {message}
        </p>
      )}

      <CreateFacilitySection
        onDone={(text) => {
          setMessage(text);
          invalidate();
        }}
      />

      {isPending && <Spinner />}
      {isError && <ErrorNote message="기관 목록을 불러오지 못했어요." onRetry={() => void refetch()} />}
      {!isPending && facilities.length === 0 && (
        <EmptyState title="해당하는 기관이 없어요" body="필터를 바꿔 보세요." />
      )}

      <ul className="flex flex-col gap-3 p-4">
        {facilities.map((facility) => (
          <FacilityCard
            key={facility.id}
            facility={facility}
            onDone={(text) => {
              setMessage(text);
              invalidate();
            }}
          />
        ))}
      </ul>
    </Screen>
  );
}

function FacilityCard({
  facility,
  onDone,
}: {
  facility: AdminFacility;
  onDone: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [showPayment, setShowPayment] = useState(false);
  const [months, setMonths] = useState('12');
  const [error, setError] = useState<string | null>(null);
  const director = facility.members[0]?.user;

  const act = useMutation({
    mutationFn: async (input: {
      path: string;
      method?: 'POST' | 'PATCH';
      body?: unknown;
      done: string;
    }) => {
      await api(input.path, { method: input.method ?? 'POST', body: input.body });
      return input.done;
    },
    onSuccess: (done) => {
      setError(null);
      setShowPayment(false);
      onDone(done);
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '처리하지 못했어요. 다시 시도해 주세요.'),
  });

  const statusTone =
    facility.status === 'APPROVED' ? 'primary' : facility.status === 'PENDING' ? 'accent' : 'neutral';

  return (
    <Card as="li" className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-lg font-bold">{facility.name}</p>
        <Chip tone={statusTone}>{FACILITY_STATUS_LABELS[facility.status]}</Chip>
      </div>

      <div className="text-sm text-ink-sub">
        {director ? (
          <p>
            원장 {director.name} · {director.email}
            {director.phone ? ` · ${formatPhone(director.phone)}` : ''}
          </p>
        ) : (
          <p>원장 정보 없음</p>
        )}
        <p className="mt-0.5">
          원아 {formatCount(facility._count.children)}명 · 구성원 {formatCount(facility._count.members)}명 ·{' '}
          {formatDate(facility.createdAt)} 등록
        </p>
        {facility.subscription && (
          <p className="mt-0.5">
            구독{' '}
            {SUBSCRIPTION_STATUS_LABELS[
              facility.subscriptionStatus as keyof typeof SUBSCRIPTION_STATUS_LABELS
            ] ?? facility.subscriptionStatus}
            {facility.subscription.plan ? ` · ${facility.subscription.plan.name}` : ''} ·{' '}
            {formatDate(facility.subscription.expiresAt)} 만료
          </p>
        )}
      </div>

      {error && (
        <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {facility.status === 'PENDING' && (
          <Button
            size="sm"
            loading={act.isPending}
            onClick={() =>
              act.mutate({
                path: `/admin/facilities/${facility.id}/approve`,
                done: `${facility.name}을(를) 승인했어요. 체험 구독이 시작됩니다.`,
              })
            }
          >
            승인
          </Button>
        )}
        {facility.status === 'APPROVED' && (
          <Button
            size="sm"
            variant="outline"
            loading={act.isPending}
            onClick={() => {
              if (!window.confirm(`${facility.name}을(를) 정지할까요? 구성원 로그인은 유지되지만 이용이 제한됩니다.`)) return;
              act.mutate({
                path: `/admin/facilities/${facility.id}/status`,
                method: 'PATCH',
                body: { status: 'SUSPENDED' },
                done: `${facility.name}을(를) 정지했어요.`,
              });
            }}
          >
            정지
          </Button>
        )}
        {facility.status === 'SUSPENDED' && (
          <Button
            size="sm"
            loading={act.isPending}
            onClick={() =>
              act.mutate({
                path: `/admin/facilities/${facility.id}/status`,
                method: 'PATCH',
                body: { status: 'APPROVED' },
                done: `${facility.name} 운영을 재개했어요.`,
              })
            }
          >
            운영 재개
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => setShowPayment((prev) => !prev)}>
          입금 승인
        </Button>
        <Button
          size="sm"
          variant="outline"
          loading={act.isPending}
          onClick={async () => {
            try {
              const invite = await apiData<{ code: string; expiresAt: string }>(
                `/admin/facilities/${facility.id}/director-invite`,
                { method: 'POST' },
              );
              onDone(
                `${facility.name} 원장 초대코드: ${invite.code} (${formatDate(invite.expiresAt)}까지) — 이 코드로 가입하면 기관에 원장으로 연결돼요.`,
              );
            } catch (caught) {
              setError(caught instanceof ApiError ? caught.message : '초대코드를 만들지 못했어요.');
            }
          }}
        >
          원장 초대코드
        </Button>
        <Link to={`/billing?facilityId=${facility.id}`}>
          <Button size="sm" variant="outline">
            문서 발행
          </Button>
        </Link>
        <Button
          size="sm"
          variant="outline"
          loading={act.isPending}
          onClick={() =>
            act.mutate({
              path: `/admin/facilities/${facility.id}/photo-policy`,
              method: 'PATCH',
              body: { allowPhotoDownload: !facility.allowPhotoDownload },
              done: `${facility.name}의 사진 원본 다운로드를 ${facility.allowPhotoDownload ? '차단' : '허용'}으로 바꿨어요.`,
            })
          }
        >
          원본 다운로드 {facility.allowPhotoDownload ? '허용됨' : '차단됨'}
        </Button>
      </div>

      {showPayment && (
        <div className="flex items-end gap-2 rounded-sm bg-bg p-3">
          {/* gap-0 래퍼 — Field 공통 gap 가드로 mb-3 상쇄, items-end 바닥선 정렬 */}
          <div className="flex w-28 shrink-0 flex-col gap-0">
            <Field label="연장 개월">
              <Input
                type="number"
                min={1}
                value={months}
                onChange={(e) => setMonths(e.target.value)}
              />
            </Field>
          </div>
          <Button
            loading={act.isPending}
            onClick={() =>
              act.mutate(
                {
                  path: `/admin/facilities/${facility.id}/subscription/approve-payment`,
                  body: { months: Number(months) || 12 },
                  done: `${facility.name} 입금을 승인하고 ${Number(months) || 12}개월 연장했어요.`,
                },
                {
                  onSuccess: () =>
                    void queryClient.invalidateQueries({ queryKey: ['admin', 'billing-documents'] }),
                },
              )
            }
          >
            승인
          </Button>
        </div>
      )}
    </Card>
  );
}

/**
 * 거래 기관 직접 등록 (§3.3) — 아직 가입하지 않은 고객사에도 견적서를
 * 발행할 수 있게 관리자가 기관을 만든다. 등록 즉시 승인 + 체험 구독 상태.
 */
function CreateFacilitySection({ onDone }: { onDone: (message: string) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      apiData<{ id: string; name: string }>('/admin/facilities', {
        method: 'POST',
        body: {
          name: name.trim(),
          phone: phone.trim() || undefined,
          address: address.trim() || undefined,
        },
      }),
    onSuccess: (facility) => {
      setError(null);
      setOpen(false);
      setName('');
      setPhone('');
      setAddress('');
      onDone(
        `${facility.name}을(를) 등록했어요. 문서 발행이 바로 가능하고, "원장 초대코드"로 고객사를 연결할 수 있어요.`,
      );
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '기관을 등록하지 못했어요.'),
  });

  return (
    <div className="px-4 pt-3">
      {!open ? (
        <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
          + 거래 기관 등록
        </Button>
      ) : (
        <Card className="flex flex-col gap-3">
          <p className="font-semibold">거래 기관 등록</p>
          <p className="text-sm text-ink-sub">
            아직 앱에 가입하지 않은 고객사예요. 등록하면 견적서·거래명세서를 발행할 수 있고,
            나중에 원장 초대코드로 연결합니다.
          </p>
          <Field label="기관 이름" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="예: 푸른숲어린이집" />
          </Field>
          <div className="flex gap-2">
            <Field label="전화번호">
              <Input value={phone} onChange={(e) => setPhone(formatPhoneInput(e.target.value))} placeholder="053-000-0000" />
            </Field>
            <div className="flex min-w-0 flex-1 flex-col gap-0">
              <Field label="주소">
                <Input value={address} onChange={(e) => setAddress(e.target.value)} />
              </Field>
            </div>
          </div>
          {error && (
            <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <Button loading={create.isPending} disabled={!name.trim()} onClick={() => create.mutate()}>
              등록
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              닫기
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
